/**
 * A transcript passage in the entity panel: what its node says, as the panel shows it.
 *
 * A passage is about a minute of a recording, written to the graph next to that recording
 * (`(AttachedDocument)-[:HAS_PASSAGE]->(TranscriptPassage)`). The node carries its own turns as
 * parallel lists — a node property holds no nested value — so the panel needs no other read:
 * `turn_starts_ms`, `turn_voices` (0 = no voice was recognised) and `turn_texts`, one entry per
 * turn. A node whose lists do not line up is shown by its stored `text` instead of by a guess.
 *
 * The one thing the node does not say is whose a voice is: that is decided on the recording's
 * page and resolved when the node is read, so the read sends a legend beside the properties
 * (`speakers`) and the panel names the voices it lists. Nothing stored changes.
 *
 * The passage is evidence only in its recording, so its one action leads there: the recording
 * page of a HUMINT capture takes a time (`?t=<whole seconds>`), any other recording opens as the
 * case document it is.
 *
 * Pure: properties in, a view model out. No router, no data.
 */

import { formatOffset } from '@/components/humint/format'
import { PASSAGE_LABEL } from '@/components/neo4j/cytoscape/types'
import { type PassageSpeakers, passageSpeakers } from '@/features/humint/recording/passage-speakers'
import { type PassageSource, passageSource } from '@/features/humint/recording/recording-path'

export type { PassageSource }

/** One turn of a passage: who speaks from when, and what is said. */
export interface PassageTurn {
  /** Its place in the passage, from 0. */
  index: number
  startMs: number
  /** Where the turn starts, as the recording page says it: "12:30", "1:02:30" past the hour. */
  time: string
  /** The voice's number on the recording page; 0 when none was recognised. */
  voice: number
  text: string
}

export interface PassageDetail {
  /** In the recording's order; empty when the node's lists cannot be read as turns. */
  turns: readonly PassageTurn[]
  /** Whether any turn was given to a voice. Without one, naming voices says nothing. */
  voiced: boolean
  /** Who each named voice is said to be, by voice; a voice not in it is a number. */
  speakers: PassageSpeakers
  /** The passage as its stored lines, for a node whose turns cannot be read. */
  text: string | null
  /** The language of the transcript, for the reader's voice. */
  language: string | null
  /** Where the passage starts in its recording: "12:30". Null when the node does not say. */
  startsAt: string | null
  source: PassageSource | null
}

export function isPassage(labels: readonly string[]): boolean {
  return labels.includes(PASSAGE_LABEL)
}

/** A property value as a non-empty string, else null. */
function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

/** A property value as a place in a recording, else null. */
function milliseconds(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

/** A property value as a list whose every entry passes `is`, else null. */
function listOf<T>(value: unknown, is: (entry: unknown) => entry is T): readonly T[] | null {
  return Array.isArray(value) && value.every(is) ? value : null
}

const isStart = (entry: unknown): entry is number => milliseconds(entry) !== null
const isVoice = (entry: unknown): entry is number =>
  typeof entry === 'number' && Number.isInteger(entry) && entry >= 0
const isText = (entry: unknown): entry is string => typeof entry === 'string'

/** The turns the node carries; none unless its three lists are whole and of one length. */
function passageTurns(properties: Record<string, unknown>): PassageTurn[] {
  const starts = listOf(properties.turn_starts_ms, isStart)
  const voices = listOf(properties.turn_voices, isVoice)
  const texts = listOf(properties.turn_texts, isText)
  if (!starts || !voices || !texts) return []
  if (starts.length !== voices.length || starts.length !== texts.length) return []
  return starts.map((startMs, index) => ({
    index,
    startMs,
    time: formatOffset(startMs),
    voice: voices[index],
    text: texts[index],
  }))
}

/**
 * What the panel shows of a passage node, from the node's own properties. A passage of a HUMINT
 * capture opens its recording page at the passage's start; any other recording has no page that
 * takes a time, and opens as the document it is (`passageSource`, which decides it for the
 * search hits too). `legend` is the read's `speakers`: the voices somebody named.
 */
export function passageDetail(
  caseId: string,
  properties: Record<string, unknown>,
  legend?: unknown
): PassageDetail {
  const turns = passageTurns(properties)
  // The start the node states, else its first turn's.
  const startMs = milliseconds(properties.start_ms) ?? turns[0]?.startMs ?? null
  return {
    turns,
    voiced: turns.some(turn => turn.voice > 0),
    speakers: passageSpeakers(legend),
    text: text(properties.text),
    language: text(properties.language),
    startsAt: startMs === null ? null : formatOffset(startMs),
    source: passageSource(caseId, {
      captureId: text(properties.capture_id),
      documentId: text(properties.document_id),
      // The page takes whole seconds and rounds down: the second the time on the button names.
      atSeconds: startMs === null ? null : startMs / 1000,
    }),
  }
}
